import { useState } from "react"
import { Layout } from "../components/Layout"
import { PageHeader } from "@/components/PageHeader"
import { Button } from "@/components/Button"
import { Card } from "@/components/Card"
import { useSubscriptionPlans, type SubscriptionPlan, type SubscriptionPlanVersion } from "@/hooks/useSubscriptionPlans"
import { useMutation } from "@tanstack/react-query"
import { client } from "@/api"
import { useToast } from "@/components/Toast"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  // DialogDescription,
} from "@/components/Dialog"
import {
  DocumentArrowDownIcon,
  CheckCircleIcon,
  UserGroupIcon,
  ServerIcon,
  VideoCameraIcon,
  ArrowPathIcon,
  ClockIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline"

interface SyncResult {
  planId: string
  planName: string
  action: "created" | "updated" | "refreshed" | "skipped" | "error"
  stripePriceId?: string | null
  stripeProductId?: string | null
  newVersion?: number
  previousVersion?: number | null
  priceAction?: "created" | "reused" | "unchanged"
  notes?: string[]
  error?: string
}

interface SyncSummary {
  success: boolean
  results: SyncResult[]
  summary: {
    created: number
    updated: number
    refreshed: number
    skipped: number
    errors: number
    versionsCreated: number
    pricesCreated: number
  }
  environment: "test" | "live"
}

export function SubscriptionPlans() {
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null)
  const [showSyncModal, setShowSyncModal] = useState(false)
  const [syncResults, setSyncResults] = useState<SyncSummary | null>(null)
  const [dryRun, setDryRun] = useState(false)

  const { data: plansData, isLoading: plansLoading, refetch: refetchPlans } = useSubscriptionPlans()
  const { toast } = useToast()

  const subscriptionPlans: SubscriptionPlan[] = plansData?.plans || []
  const activePlan =
    (selectedPlan
      ? subscriptionPlans.find((plan) => plan.id === selectedPlan)
      : subscriptionPlans[0]) || null
  const selectedPlanId = activePlan?.id ?? null
  const selectedPlanVersions = activePlan?.versions ?? []

  // Stripe sync mutation
  const syncMutation = useMutation({
    mutationFn: async (dryRun: boolean) => {
      const response = await client.admin["subscription-plans"].sync.$post({
        json: { dryRun },
      })

      if (!response.ok) {
        throw new Error(`Publish failed: ${response.status}`)
      }

      return response.json() as Promise<SyncSummary>
    },
    onSuccess: (data) => {
      setSyncResults(data)
      if (data.success) {
        toast({
          title: "Pricing publish completed successfully",
          variant: "default",
        })
        refetchPlans()
      } else {
        toast({
          title: "Pricing publish completed with errors",
          variant: "destructive",
        })
      }
    },
    onError: (error) => {
      toast({
        title: `Publish failed: ${error.message}`,
        variant: "destructive",
      })
    },
  })

  // Check if any plans don't exist in Stripe yet
  const hasPlansWithoutStripe = subscriptionPlans.some(
    (plan) => !plan.stripe_price_id_yearly && plan.tier !== "enterprise"
  )

  // Real counts from the API: one grouped query over team_subscriptions (see
  // countEntitledTeamsByPlan). "paying" counts distinct purchases, excluding comps and trials;
  // one organization purchase may cover several entitled teams.
  const countsFor = (plan: SubscriptionPlan) =>
    plan.team_counts ?? { teams: 0, comped: 0, paying: 0 }
  const totals = subscriptionPlans.reduce(
    (acc, plan) => {
      const counts = countsFor(plan)
      return {
        teams: acc.teams + counts.teams,
        comped: acc.comped + counts.comped,
        paying: acc.paying + counts.paying,
        listPriceArrCents: acc.listPriceArrCents + counts.paying * (plan.price_yearly ?? 0),
      }
    },
    { teams: 0, comped: 0, paying: 0, listPriceArrCents: 0 }
  )

  const exportReport = () => {
    // Generate CSV report
    const headers = ["Plan", "Teams", "Paid subscriptions", "Comped teams", "List price / year", "List-price ARR"]
    const rows = subscriptionPlans.map((plan: SubscriptionPlan) => {
      const counts = countsFor(plan)
      const listPrice = plan.price_yearly == null ? "Custom" : (plan.price_yearly / 100).toFixed(2)
      const arr =
        plan.price_yearly == null ? "Custom" : ((counts.paying * plan.price_yearly) / 100).toFixed(2)
      return [plan.name, counts.teams, counts.paying, counts.comped, listPrice, arr]
    })

    const csv = [headers, ...rows].map(row => row.join(",")).join("\n")
    const blob = new Blob([csv], { type: "text/csv" })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `subscription-report-${new Date().toISOString().split("T")[0]}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
  }

  const formatLimit = (value: number, suffix: string = "") => {
    // subscription_plans stores "unlimited" as 9999 (seed migration 0035)
    if (value === -1 || value >= 9999) return "Unlimited"
    return `${value}${suffix}`
  }

  const getSyncStatusIcon = (plan: SubscriptionPlan) => {
    const derivedStatus =
      plan.sync_status ??
      (plan.needs_publish ? "draft" : plan.stripe_sync_status ?? "pending")

    if (derivedStatus === "draft" || plan.needs_publish) {
      return (
        <div
          className="flex items-center text-amber-400"
          title="Updates saved locally. Publish to update Stripe checkout."
        >
          <ClockIcon className="h-4 w-4 mr-1" />
          <span className="text-xs">Needs publish</span>
        </div>
      )
    }

    if (derivedStatus === "synced" && plan.stripe_price_id_yearly) {
      return (
        <div
          className="flex items-center text-green-500"
          title={`Synced: ${plan.last_stripe_sync ? new Date(plan.last_stripe_sync).toLocaleString() : "Never"}`}
        >
          <CheckCircleIcon className="h-4 w-4 mr-1" />
          <span className="text-xs">Synced</span>
        </div>
      )
    } else if (derivedStatus === "error") {
      return (
        <div
          className="flex items-center text-red-500"
          title={plan.stripe_sync_error || "Sync error"}
        >
          <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
          <span className="text-xs">Error</span>
        </div>
      )
    } else {
      return (
        <div className="flex items-center text-yellow-500" title="Not synced with Stripe">
          <ClockIcon className="h-4 w-4 mr-1" />
          <span className="text-xs">Pending</span>
        </div>
      )
    }
  }

  return (
    <Layout>
      <div className="space-y-6">
        <PageHeader
          title="Subscription Plans"
          actions={
            <div className="flex items-center space-x-3">
              <Button
                onClick={() => setShowSyncModal(true)}
                variant="secondary"
                disabled={syncMutation.isPending}
              >
                {syncMutation.isPending ? (
                  <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <ArrowPathIcon className="h-4 w-4 mr-2" />
                )}
                {hasPlansWithoutStripe ? "Create plans in Stripe" : "Publish Pricing Update"}
              </Button>
              <Button onClick={exportReport}>
                <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                Export Report
              </Button>
            </div>
          }
        />

        {/* Plans Comparison Table */}
        <div className="bg-surface rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4">Available Plans</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {plansLoading ? (
              <div className="col-span-full text-center py-8 text-secondary">Loading plans...</div>
            ) : (
              subscriptionPlans.map((plan) => (
                <Card 
                  key={plan.id} 
                  className={`p-4 cursor-pointer transition-all ${
                    selectedPlanId === plan.id 
                      ? "ring-2 ring-primary" 
                      : "hover:border-primary/50"
                  }`}
                  onClick={() => setSelectedPlan(plan.id)}
                >
                  <div className="space-y-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold">{plan.name}</h3>
                        <p className="text-sm text-secondary capitalize">{plan.tier} Tier</p>
                      </div>
                      {getSyncStatusIcon(plan)}
                    </div>

                    <div className="text-2xl font-bold">
                      {plan.price_yearly ? (
                        <span>
                          ${(plan.price_yearly / 100).toFixed(0)}
                          <span className="text-sm font-normal text-secondary">/year</span>
                        </span>
                      ) : (
                        <span className="text-secondary">Custom</span>
                      )}
                    </div>

                    <div className="flex items-center justify-between text-xs text-secondary">
                      <span>
                        Version{" "}
                        <span className="font-medium text-primary">
                          {plan.current_version ?? "—"}
                        </span>
                      </span>
                      <span
                        className="truncate max-w-[160px] text-[11px]"
                        title={plan.stripe_price_id_yearly ?? "No Stripe price linked"}
                      >
                        {plan.stripe_price_id_yearly ?? "No Stripe price"}
                      </span>
                    </div>

                    <div className="space-y-2 text-sm">
                      <div className="flex items-center space-x-2">
                        <ServerIcon className="h-4 w-4 text-secondary" />
                        <span>{formatLimit(plan.max_storage_gb, " GB")}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <VideoCameraIcon className="h-4 w-4 text-secondary" />
                        <span>{formatLimit(plan.max_games)} games</span>
                      </div>
                      {plan.max_team_members !== null && plan.max_team_members !== 0 && (
                        <div className="flex items-center space-x-2">
                          <UserGroupIcon className="h-4 w-4 text-secondary" />
                          <span>{formatLimit(plan.max_team_members)} members</span>
                        </div>
                      )}

                      {/* Teams allowance for the plan */}
                      <div className="flex items-center space-x-2">
                        <UserGroupIcon className="h-4 w-4 text-secondary" />
                        <span>
                          {(() => {
                            const mt = (plan as any).max_teams
                            if (mt === null || mt === undefined) {
                              return plan.tier === "enterprise" ? "Custom teams" : "—"
                            }
                            if (mt === 1) return "1 team"
                            return `Up to ${mt} teams`
                          })()}
                        </span>
                      </div>
                    </div>

                    <div className="pt-2 border-t">
                      <p className="text-xs text-secondary font-medium mb-1">Teams on this plan:</p>
                      <p className="text-lg font-semibold">{countsFor(plan).teams}</p>
                      <p className="text-xs text-secondary">
                        {countsFor(plan).paying} paid subscriptions · {countsFor(plan).comped} comped
                      </p>
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>
        </div>

        {/* Plan Features */}
        <div className="bg-surface rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4">Plan Features Comparison</h2>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4">Feature</th>
                  {subscriptionPlans.map((plan: SubscriptionPlan) => (
                    <th key={plan.id} className="text-center py-3 px-4">
                      {plan.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="py-3 px-4">Storage</td>
                  {subscriptionPlans.map((plan: SubscriptionPlan) => (
                    <td key={plan.id} className="text-center py-3 px-4">
                      {formatLimit(plan.max_storage_gb, " GB")}
                    </td>
                  ))}
                </tr>
                <tr className="border-b">
                  <td className="py-3 px-4">Games</td>
                  {subscriptionPlans.map((plan: SubscriptionPlan) => (
                    <td key={plan.id} className="text-center py-3 px-4">
                      {formatLimit(plan.max_games)}
                    </td>
                  ))}
                </tr>
                <tr className="border-b">
                  <td className="py-3 px-4">Team Members</td>
                  {subscriptionPlans.map((plan: SubscriptionPlan) => (
                    <td key={plan.id} className="text-center py-3 px-4">
                      {plan.max_team_members === null || plan.max_team_members === 0
                        ? "—"
                        : formatLimit(plan.max_team_members)}
                    </td>
                  ))}
                </tr>

                <tr className="border-b">
                  <td className="py-3 px-4">Teams</td>
                  {subscriptionPlans.map((plan: SubscriptionPlan) => (
                    <td key={plan.id} className="text-center py-3 px-4">
                      {(plan as any).max_teams === null || (plan as any).max_teams === undefined
                        ? plan.tier === "enterprise"
                          ? "Custom"
                          : "—"
                        : (plan as any).max_teams === 1
                          ? "1 team"
                          : `Up to ${(plan as any).max_teams} teams`}
                    </td>
                  ))}
                </tr>
                {/* Feature rows */}
                {["prioritySupport", "sla"].map(feature => (
                  <tr key={feature} className="border-b">
                    <td className="py-3 px-4">
                      {feature === "prioritySupport" ? "Priority Support" : 
                       feature === "sla" ? "SLA Guarantee" : 
                       feature.charAt(0).toUpperCase() + feature.slice(1)}
                    </td>
                    {subscriptionPlans.map((plan: SubscriptionPlan) => (
                      <td key={plan.id} className="text-center py-3 px-4">
                        {plan.features[feature] ? (
                          <CheckCircleIcon className="h-5 w-5 text-green-500 mx-auto" />
                        ) : (
                          <span className="text-secondary">—</span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Plan Version History */}
        {activePlan && (
          <div className="bg-surface rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold">Plan Version History</h2>
                <p className="text-sm text-secondary">
                  {activePlan.name} · current version{" "}
                  <span className="font-medium text-primary">
                    v{activePlan.current_version ?? "—"}
                  </span>
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-secondary">Stripe price ID</p>
                <p
                  className="text-xs font-mono text-secondary max-w-[220px] truncate"
                  title={activePlan.stripe_price_id_yearly ?? "No Stripe price"}
                >
                  {activePlan.stripe_price_id_yearly ?? "No Stripe price"}
                </p>
              </div>
            </div>

            {selectedPlanVersions.length === 0 ? (
              <p className="text-sm text-secondary">
                No version snapshots recorded yet. Publish a pricing update to create the first
                version.
              </p>
            ) : (
              <div className="space-y-3">
                {selectedPlanVersions.map((version: SubscriptionPlanVersion) => {
                  const priceLabel =
                    version.price_yearly && version.price_yearly > 0
                      ? `$${(version.price_yearly / 100).toFixed(2)}/year`
                      : "Custom pricing"
                  const effectiveToLabel = version.effective_to
                    ? new Date(version.effective_to).toLocaleDateString()
                    : "Present"

                  return (
                    <div
                      key={version.id}
                      className="border border-border rounded-lg p-4 bg-surface-hover"
                    >
                      <div className="flex items-center justify-between text-sm">
                        <div className="font-semibold">
                          v{version.version} · {priceLabel}
                        </div>
                        <div className="text-xs text-secondary">
                          {new Date(version.effective_from).toLocaleDateString()} → {effectiveToLabel}
                        </div>
                      </div>
                      <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-secondary">
                        <div>Games: {version.max_games}</div>
                        <div>Storage: {version.max_storage_gb} GB</div>
                        {version.max_team_members !== null && (
                          <div>Members: {formatLimit(version.max_team_members)}</div>
                        )}
                        <div className="col-span-2 truncate">
                          Stripe price: {version.stripe_price_id ?? "—"}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}

        {/* Subscription Metrics */}
        <div className="bg-surface rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4">Subscription Metrics</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="p-4">
              <p className="text-sm text-secondary">Teams with a plan</p>
              <p className="text-2xl font-semibold">{totals.teams}</p>
            </Card>
            <Card className="p-4">
              <p className="text-sm text-secondary">Paid subscriptions</p>
              <p className="text-2xl font-semibold">{totals.paying}</p>
              <p className="text-xs text-secondary">
                ${(totals.listPriceArrCents / 100).toLocaleString()} / year at list price
              </p>
            </Card>
            <Card className="p-4">
              <p className="text-sm text-secondary">Comped teams</p>
              <p className="text-2xl font-semibold">{totals.comped}</p>
            </Card>
          </div>
        </div>
      </div>

      {/* Publish Pricing Dialog */}
      <Dialog
        open={showSyncModal}
        onOpenChange={(open) => {
          if (!open) {
            setSyncResults(null)
          }
          setShowSyncModal(open)
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Publish Pricing Update</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {!syncResults ? (
              <>
                {/* Confirmation Step */}
                <div className="text-sm text-secondary">
                  <p className="mb-4">
                    The database is the source of truth. Publishing will push the current pricing
                    and limits to Stripe and capture a new immutable plan version:
                  </p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Create Stripe products when a plan is missing one</li>
                    <li>Archive legacy yearly prices and create new ones if the DB amount changed</li>
                    <li>Record a new plan version so existing subscribers remain on their original price</li>
                    <li>Update the plan&#39;s current Stripe price ID in the database</li>
                  </ul>
                </div>

                <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
                  <div className="flex items-start space-x-2">
                    <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-yellow-500">Important</p>
                      <p className="text-secondary mt-1">
                        Enterprise plans use custom pricing and are skipped. Existing subscribers keep
                        their current version—new checkout sessions will use the version that you
                        publish here.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="rounded border-gray-600 text-primary focus:ring-primary"
                      checked={dryRun}
                      onChange={(e) => setDryRun(e.target.checked)}
                    />
                    <span className="text-sm">Dry run (preview changes without applying)</span>
                  </label>

                  <div className="flex items-center space-x-3">
                    <Button
                      variant="secondary"
                      onClick={() => setShowSyncModal(false)}
                      disabled={syncMutation.isPending}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={() => syncMutation.mutate(dryRun)}
                      disabled={syncMutation.isPending}
                    >
                      {syncMutation.isPending ? (
                        <>
                          <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                          Publishing...
                        </>
                      ) : (
                        <>
                          <ArrowPathIcon className="h-4 w-4 mr-2" />
                          {dryRun ? "Preview Publish" : "Publish Update"}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Results Display */}
                <div className="space-y-4">
                  {/* Summary */}
                  <div
                    className={`p-4 rounded-lg ${
                      syncResults.success
                        ? "bg-green-500/10 border border-green-500/20"
                        : "bg-red-500/10 border border-red-500/20"
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      {syncResults.success ? (
                        <CheckCircleIcon className="h-5 w-5 text-green-500" />
                      ) : (
                        <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
                      )}
                      <span
                        className={`font-medium ${
                          syncResults.success ? "text-green-500" : "text-red-500"
                        }`}
                      >
                        Publish {syncResults.success ? "Completed" : "Completed with Errors"}
                      </span>
                      <span className="text-xs text-secondary">
                        ({syncResults.environment === "test" ? "Test Mode" : "Live Mode"})
                      </span>
                    </div>

                    {dryRun && (
                      <p className="text-xs text-secondary mt-2">
                        Preview only — no changes were applied.
                      </p>
                    )}

                    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4 mt-4">
                      <div className="text-center">
                        <p className="text-2xl font-semibold text-green-500">
                          {syncResults.summary.created}
                        </p>
                        <p className="text-xs text-secondary">Created</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-semibold text-blue-500">
                          {syncResults.summary.updated}
                        </p>
                        <p className="text-xs text-secondary">Updated</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-semibold text-sky-500">
                          {syncResults.summary.refreshed ?? 0}
                        </p>
                        <p className="text-xs text-secondary">Refreshed</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-semibold text-gray-500">
                          {syncResults.summary.skipped}
                        </p>
                        <p className="text-xs text-secondary">Skipped</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-semibold text-red-500">
                          {syncResults.summary.errors}
                        </p>
                        <p className="text-xs text-secondary">Errors</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-semibold text-purple-500">
                          {syncResults.summary.versionsCreated}
                        </p>
                        <p className="text-xs text-secondary">Versions</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-semibold text-amber-500">
                          {syncResults.summary.pricesCreated}
                        </p>
                        <p className="text-xs text-secondary">New Prices</p>
                      </div>
                    </div>
                  </div>

                  {/* Detailed Results */}
                  <div className="space-y-2">
                    <h4 className="font-medium text-sm">Detailed Results:</h4>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {syncResults.results.map((result, index) => (
                        <div
                          key={index}
                          className={`p-3 rounded-lg text-sm ${
                            result.action === "error"
                              ? "bg-red-500/10 border border-red-500/20"
                              : "bg-surface-hover border border-border"
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-center space-x-2">
                              {result.action === "created" && (
                                <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0" />
                              )}
                              {result.action === "updated" && (
                                <ArrowPathIcon className="h-4 w-4 text-blue-500 flex-shrink-0" />
                              )}
                              {result.action === "refreshed" && (
                                <ArrowPathIcon className="h-4 w-4 text-sky-500 flex-shrink-0" />
                              )}
                              {result.action === "skipped" && (
                                <ClockIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
                              )}
                              {result.action === "error" && (
                                <ExclamationTriangleIcon className="h-4 w-4 text-red-500 flex-shrink-0" />
                              )}
                              <span className="font-medium">{result.planName}</span>
                            </div>
                            <span
                              className={`text-xs capitalize ${
                                result.action === "created"
                                  ? "text-green-500"
                                  : result.action === "updated"
                                    ? "text-blue-500"
                                    : result.action === "refreshed"
                                      ? "text-sky-500"
                                      : result.action === "skipped"
                                      ? "text-gray-500"
                                      : "text-red-500"
                              }`}
                            >
                              {result.action}
                            </span>
                          </div>

                          {result.stripePriceId && (
                            <p className="text-xs text-secondary mt-1 ml-6">
                              Price ID: {result.stripePriceId}
                            </p>
                          )}

                          {result.stripeProductId && (
                            <p className="text-xs text-secondary mt-1 ml-6">
                              Product ID: {result.stripeProductId}
                            </p>
                          )}

                          {typeof result.newVersion !== "undefined" && (
                            <p className="text-xs text-secondary mt-1 ml-6">
                              Version published: v{result.newVersion}
                              {typeof result.previousVersion === "number"
                                ? ` (previous v${result.previousVersion})`
                                : ""}
                            </p>
                          )}

                          {result.priceAction && (
                            <p className="text-xs text-secondary mt-1 ml-6">
                              Price action:{" "}
                              {result.priceAction === "created"
                                ? "created new price"
                                : result.priceAction === "reused"
                                  ? "reused existing price"
                                  : "unchanged"}
                            </p>
                          )}

                          {result.notes && result.notes.length > 0 && (
                            <ul className="text-xs text-secondary mt-2 ml-6 list-disc list-inside space-y-1">
                              {result.notes.map((note, noteIndex) => (
                                <li key={noteIndex}>{note}</li>
                              ))}
                            </ul>
                          )}

                          {result.error && (
                            <p className="text-xs text-red-400 mt-1 ml-6">{result.error}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end pt-4 border-t">
                    <Button
                      onClick={() => {
                        setShowSyncModal(false)
                        setSyncResults(null)
                        setDryRun(false)
                      }}
                    >
                      Close
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  )
}
